import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Send, Building2, Phone, Users2, UserCog } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export const Kontakt = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState({});

    useEffect(() => {
        api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
    }, []);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/contact", form);
            toast.success(data.message || "Wiadomość została wysłana.");
            setForm({ name: "", email: "", phone: "", message: "", website: "" });
        } catch (err) {
            if (err.response?.status === 429) {
                toast.error("Zbyt wiele prób. Spróbuj ponownie za chwilę.");
            } else {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Nie udało się wysłać wiadomości.");
            }
        } finally {
            setLoading(false);
        }
    };

    const hasZarzad = settings.zarzad_email || settings.zarzad_phone || settings.zarzad_name;
    const hasAdminOsiedla = settings.admin_osiedla_email || settings.admin_osiedla_phone || settings.admin_osiedla_name;

    return (
        <section id="kontakt" data-testid="kontakt-section" className="py-24 md:py-32 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl mb-14"
                >
                    <p className="text-[#D4A537] uppercase tracking-[0.25em] text-xs font-semibold mb-4">Skontaktuj się</p>
                    <h2 className="font-serif text-4xl md:text-5xl text-[#092E22] leading-tight mb-4">
                        Kontakt
                    </h2>
                    <div className="divider-gold w-24 mb-6" />
                    <p className="text-gray-600 text-base md:text-lg">
                        Masz pytanie lub sprawę do załatwienia? Napisz do nas – odpowiemy najszybciej, jak to możliwe.
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-5 gap-8">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-2 bg-[#0D4F3C] text-white rounded-3xl p-8 md:p-10 relative overflow-hidden"
                    >
                        <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full bg-[#D4A537]/20 blur-3xl" />
                        <div className="relative space-y-7">
                            <div>
                                <h3 className="font-serif text-2xl mb-2">Dane kontaktowe</h3>
                                <div className="divider-gold w-12" />
                            </div>

                            <ContactItem Icon={Building2} title="Adres">
                                ul. Aluzyjna 25 C/D/E/F<br />
                                03-149 Warszawa, Białołęka
                            </ContactItem>

                            <ContactItem Icon={Mail} title="Administrator strony">
                                <a href={`mailto:${settings.site_admin_email || "portal@aluzyjna25cdef.pl"}`} className="hover:text-[#D4A537] underline-offset-4 hover:underline" data-testid="kontakt-admin-email">
                                    {settings.site_admin_email || "portal@aluzyjna25cdef.pl"}
                                </a>
                            </ContactItem>

                            <ContactItem Icon={Users2} title="Zarząd wspólnoty" testid="kontakt-zarzad">
                                {hasZarzad ? (
                                    <div className="space-y-1">
                                        {settings.zarzad_name && <p>{settings.zarzad_name}</p>}
                                        {settings.zarzad_email && (
                                            <p><a href={`mailto:${settings.zarzad_email}`} className="hover:text-[#D4A537] underline-offset-4 hover:underline" data-testid="kontakt-zarzad-email">{settings.zarzad_email}</a></p>
                                        )}
                                        {settings.zarzad_phone && (
                                            <p className="flex items-center gap-1.5"><Phone size={12} className="text-[#D4A537]" /> <a href={`tel:${settings.zarzad_phone.replace(/\s+/g, "")}`} className="hover:text-[#D4A537]" data-testid="kontakt-zarzad-phone">{settings.zarzad_phone}</a></p>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-white/60 text-xs italic">Dane zostaną wkrótce uzupełnione przez administratora.</p>
                                )}
                            </ContactItem>

                            <ContactItem Icon={UserCog} title="Administrator osiedla" testid="kontakt-admin-osiedla">
                                {hasAdminOsiedla ? (
                                    <div className="space-y-1">
                                        {settings.admin_osiedla_name && <p>{settings.admin_osiedla_name}</p>}
                                        {settings.admin_osiedla_email && (
                                            <p><a href={`mailto:${settings.admin_osiedla_email}`} className="hover:text-[#D4A537] underline-offset-4 hover:underline" data-testid="kontakt-admin-osiedla-email">{settings.admin_osiedla_email}</a></p>
                                        )}
                                        {settings.admin_osiedla_phone && (
                                            <p className="flex items-center gap-1.5"><Phone size={12} className="text-[#D4A537]" /> <a href={`tel:${settings.admin_osiedla_phone.replace(/\s+/g, "")}`} className="hover:text-[#D4A537]" data-testid="kontakt-admin-osiedla-phone">{settings.admin_osiedla_phone}</a></p>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-white/60 text-xs italic">Dane zostaną wkrótce uzupełnione przez administratora.</p>
                                )}
                            </ContactItem>

                            <ContactItem Icon={MapPin} title="Region">
                                Warszawa Białołęka<br />
                                <span className="text-white/60 text-sm">Dąbrówka Wiślana</span>
                            </ContactItem>
                        </div>
                    </motion.div>

                    <motion.form
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        onSubmit={onSubmit}
                        className="lg:col-span-3 bg-[#FAF8F3] rounded-3xl p-8 md:p-10 space-y-5"
                        data-testid="kontakt-form"
                    >
                        <input
                            type="text"
                            name="website"
                            value={form.website}
                            onChange={onChange("website")}
                            tabIndex={-1}
                            autoComplete="off"
                            style={{ position: "absolute", left: "-10000px", opacity: 0, pointerEvents: "none" }}
                            aria-hidden="true"
                        />
                        <div className="grid md:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <Label className="text-[#092E22] font-medium">Imię i nazwisko *</Label>
                                <Input
                                    required
                                    value={form.name}
                                    onChange={onChange("name")}
                                    data-testid="kontakt-input-name"
                                    placeholder="Jan Kowalski"
                                    className="h-12 rounded-full px-5 bg-white border-gray-200 focus-visible:ring-[#0D4F3C]"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-[#092E22] font-medium">E-mail *</Label>
                                <Input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={onChange("email")}
                                    data-testid="kontakt-input-email"
                                    placeholder="jan@example.com"
                                    className="h-12 rounded-full px-5 bg-white border-gray-200 focus-visible:ring-[#0D4F3C]"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-[#092E22] font-medium">Telefon (opcjonalnie)</Label>
                            <Input
                                value={form.phone}
                                onChange={onChange("phone")}
                                data-testid="kontakt-input-phone"
                                placeholder="+48 ..."
                                className="h-12 rounded-full px-5 bg-white border-gray-200 focus-visible:ring-[#0D4F3C]"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-[#092E22] font-medium">Treść wiadomości *</Label>
                            <Textarea
                                required
                                rows={6}
                                value={form.message}
                                onChange={onChange("message")}
                                data-testid="kontakt-input-message"
                                placeholder="Opisz swoją sprawę..."
                                className="rounded-2xl px-5 py-4 bg-white border-gray-200 focus-visible:ring-[#0D4F3C]"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="kontakt-submit-button"
                            className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full h-12 px-8 font-semibold transition-all hover:-translate-y-0.5"
                        >
                            {loading ? "Wysyłanie..." : (
                                <><Send size={16} className="mr-2" /> Wyślij wiadomość</>
                            )}
                        </Button>
                        <p className="text-xs text-gray-500">
                            Twoje dane przetwarzamy zgodnie z polityką prywatności (RODO).
                        </p>
                    </motion.form>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mt-12 rounded-3xl overflow-hidden shadow-sm border border-gray-100"
                    data-testid="kontakt-map"
                >
                    <iframe
                        title="Mapa – Aluzyjna 25, 03-149 Warszawa Białołęka"
                        src="https://www.google.com/maps?q=ul.+Aluzyjna+25,+03-149+Warszawa,+Białołęka&output=embed"
                        width="100%"
                        height="420"
                        style={{ border: 0 }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </motion.div>
            </div>
        </section>
    );
};

const ContactItem = ({ Icon, title, children, testid }) => (
    <div className="flex gap-4" data-testid={testid}>
        <div className="shrink-0 w-11 h-11 rounded-xl bg-[#D4A537]/20 text-[#D4A537] flex items-center justify-center">
            <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-[#D4A537] text-xs uppercase tracking-[0.2em] font-semibold mb-1">{title}</p>
            <div className="text-white/90 text-sm leading-relaxed">{children}</div>
        </div>
    </div>
);

export default Kontakt;
